import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { toast } from 'react-toastify';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import {GlobalCurrency} from '../../GlobalCurrency/GlobalCurrency';
import { useDebouncedPerKeySave } from '../hooks/useDebouncedPerKeySave';
import { hasPayoutCap } from '../utils/payoutCap';

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

const saveDealBracket = (csrfToken, team, dealBracket) => {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  axios.patch(`/teams/${team.id}/deal_brackets/${dealBracket.id}`, {
    deal_bracket: {
      dollar_amount: (dealBracket.dollar_amount ? parseFloatFromString(dealBracket.dollar_amount) : ""),
      upside_percentage: (dealBracket.upside_percentage ? parseFloatFromString(dealBracket.upside_percentage) : ""),
      quantity: (dealBracket.quantity ? parseInt(dealBracket.quantity) : ""),
      dollar_or_percentage_operator: dealBracket.dollar_or_percentage_operator
    }
  }).catch(error => {
    toast.error('Failed to save deal bracket changes');
  });
};

const deleteDealBracketRecord = (csrfToken, team, dealBracket) => {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  axios.delete(`/teams/${team.id}/deal_brackets/${dealBracket.id}`)
    .catch(error => {
      toast.error('Failed to delete deal bracket');
    });
};

const DealBracket = ({
  csrfToken,
  team,
  offer,
  showSecondaryDollarAmountField,
  dealBracket,
  dealBracketChanged,
  dealBrackets,
  dealBracketsChanged,
  isFirstBracket = false,
  index = 0,
  provided,
  snapshot
}) => {
  const [dollarAmount, setDollarAmount] = useState(dealBracket.dollar_amount || '');
  const [upsidePercentage, setUpsidePercentage] = useState(dealBracket.upside_percentage || '');
  const [quantity, setQuantity] = useState(dealBracket.quantity || '');
  const [secondaryDollarAmount, setSecondaryDollarAmount] = useState('');

  const debouncedSaveDealBracket = useDebouncedPerKeySave(saveDealBracket, 1000);

  const globalCurrency = team.secondary_currency_symbol && team.currency_conversion_rate ?
    new GlobalCurrency(team.currency_conversion_rate, team.secondary_currency_symbol) : null;

  useEffect(() => {
    if (!globalCurrency || !showSecondaryDollarAmountField) return;
    
    var amount = dollarAmount;
    var converted = "";

    if(amount && amount.length > 0){
      var dollarAmountFloat = parseFloatFromString(amount);
      converted = globalCurrency.convert(dollarAmountFloat).toFixed(2);
    }

    setSecondaryDollarAmount(converted);
  }, [dollarAmount, globalCurrency, showSecondaryDollarAmountField]);

  useEffect(() => {
    setDollarAmount(dealBracket.dollar_amount || '');
    setUpsidePercentage(dealBracket.upside_percentage || '');
    setQuantity(dealBracket.quantity || '');
  }, [dealBracket.dollar_amount, dealBracket.upside_percentage, dealBracket.quantity]);

  useEffect(() => {
    if (!globalCurrency || !showSecondaryDollarAmountField || !dealBracket.dollar_amount) return;

    var dollarAmountFloat = parseFloatFromString(dealBracket.dollar_amount);
    var converted = globalCurrency.convert(dollarAmountFloat).toFixed(2);
    setSecondaryDollarAmount(converted);
  }, [globalCurrency, showSecondaryDollarAmountField, dealBracket.dollar_amount]);

  const quantityMask = createNumberMask({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: false,
    allowDecimal: false,
    allowNegative: false,
    integerLimit: 10
  });

  const containerStyle = index % 2 === 0 ? 
    {"padding": "10px", "margin": "5px 0"} : 
    {"backgroundColor": "var(--od-c-fafafa, rgb(248, 249, 250))", "padding": "10px", "borderRadius": "4px", "margin": "5px 0"};

  return (
    <div style={containerStyle}>
      <div className='row'>
        <div className={"col-12"}>
          <div className="form-row"
              style={{"marginBottom": "14px"}}>
            <div className="col-xs-auto d-flex align-items-center"
              style={{"width": "30px", "paddingRight": "5px"}}>
              <a {...(provided ? provided.dragHandleProps : {})}
                 href="#"
                 onClick={
                   (e) => {
                     e.preventDefault();
                   }
                 }
                 className="btn btn-link btn-sm text-muted p-0">
                <i className="fas fa-grip-vertical"></i>
              </a>
            </div>
            <div className="col-xs-auto"
              style={{"width": "120px"}}>
              <div className="form-group mb-0">
                <label className="text-muted"
                      style={{"whiteSpace": "nowrap"}}
                      htmlFor="deal-bracket-quantity">
                  {isFirstBracket ? "First" : "Next"} {dealBracket.quantity ? parseInt(dealBracket.quantity).toLocaleString() : "0"} Tickets at
                </label>
                <MaskedInput
                  id="deal-bracket-quantity"
                  className="form-control"
                  style={{
                    "border": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                    "borderRadius": "4px"
                  }}
                  mask={quantityMask}
                  placeholder="0"
                  value={quantity || ""}
                  onChange={e => {
                    var newValue = e.target.value;
                    setQuantity(newValue);

                    var changes = { quantity: newValue };
                    var updated = { ...dealBracket, ...changes };

                    dealBracketChanged(updated);
                    debouncedSaveDealBracket(csrfToken, team, dealBracket, changes);
                  }}
                />
              </div>
            </div>
            <div className="col-xs-auto"
              style={showSecondaryDollarAmountField ? {"width": "280px"} : {"width": "160px"}}>
              <div className="form-group mb-0">
                <label className="text-muted"
                      style={{"whiteSpace": "nowrap"}}
                      htmlFor="offer-dollar-amount">
                  Guarantee
                </label>
                {showSecondaryDollarAmountField ? (
                  <div className='d-flex'>
                    <div className="input-group inline-white"
                        style={{
                          "border": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                          "borderRadius": "4px",
                          "borderTopRightRadius": "0px",
                          "borderBottomRightRadius": "0px"
                        }}>
                      <div className="input-group-prepend">
                        <span className="input-group-text px-1"
                              style={{"color": "var(--od-c-a4a7ae, #9b9b9b)"}}
                              id="offer-dollar-amount-dollar-sign">
                          <strong>{team.base_currency_symbol}</strong>
                        </span>
                      </div>
                      <CurrencyInput className="form-control px-0"
                                    maskOptions={{
                                      prefix: "",
                                      integerLimit: null
                                    }}
                                    placeholder={"0"}
                                    value={dollarAmount || ""}
                                    onChange={
                                      (e) => {
                                        var newValue = e.target.value;
                                        setDollarAmount(newValue);

                                        var changes = { dollar_amount: newValue };
                                        var updated = { ...dealBracket, ...changes };
                                        dealBracketChanged(updated);
                                        debouncedSaveDealBracket(csrfToken, team, dealBracket, changes);
                                      }
                                    }
                                    id="offer-dollar-amount"
                                    aria-describedby="offer-dollar-amount-dollar-sign" />
                    </div>
                    <div className="input-group inline-white"
                        style={{
                          "borderTop": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                          "borderRight": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                          "borderBottom": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                          "borderRadius": "4px",
                          "borderTopLeftRadius": "0px",
                          "borderBottomLeftRadius": "0px"
                        }}>
                      <div className="input-group-prepend">
                        <span className="input-group-text px-1"
                              style={{"color": "var(--od-c-a4a7ae, #9b9b9b)"}}
                              id="offer-secondary-dollar-amount-dollar-sign">
                          <strong>{team.secondary_currency_symbol}</strong>
                        </span>
                      </div>
                      <CurrencyInput className="form-control px-0"
                                    maskOptions={{
                                      prefix: "",
                                      integerLimit: null
                                    }}
                                    placeholder={"0"}
                                    value={secondaryDollarAmount || ""}
                                    onChange={
                                      (e) => {
                                        var amount = e.target.value;
                                        var converted = "";

                                        if(amount && amount.length > 0 && globalCurrency){
                                          var dollarAmountFloat = parseFloatFromString(amount);
                                          converted = globalCurrency.revert(dollarAmountFloat).toFixed(2);
                                        }

                                        var changes = { dollar_amount: converted };
                                        var updated = { ...dealBracket, ...changes };

                                        setDollarAmount(converted);
                                        setSecondaryDollarAmount(amount);
                                        dealBracketChanged(updated);
                                        debouncedSaveDealBracket(csrfToken, team, dealBracket, changes);
                                      }
                                    }
                                    id="offer-secondary-dollar-amount"
                                    aria-describedby="offer-secondary-dollar-amount-dollar-sign" />
                    </div>
                  </div>
                ) : (
                  <div className="input-group inline-white"
                      style={{
                        "border": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                        "borderRadius": "4px"
                      }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text px-2"
                            id="offer-dollar-amount-dollar-sign">
                        <i className="far fa-dollar-sign text-muted"></i>
                      </span>
                    </div>
                    <CurrencyInput
                      id="offer-dollar-amount"
                      aria-describedby="offer-dollar-amount-dollar-sign"
                      className="form-control"
                      maskOptions={{ prefix: "", integerLimit: null }}
                      placeholder={"Enter amount"}
                      value={dollarAmount || ""}
                      onChange={e => {
                        var newValue = e.target.value;
                        setDollarAmount(newValue);

                        var changes = { dollar_amount: newValue };
                        var updated = { ...dealBracket, ...changes };

                        dealBracketChanged(updated);
                        debouncedSaveDealBracket(csrfToken, team, dealBracket, changes);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col-xs-auto d-flex align-items-end"
              style={{"paddingBottom": "7px", "paddingLeft": "5px", "paddingRight": "5px"}}>
              <div className="btn-group btn-group-sm"
                  style={{
                    "border": "1px solid var(--od-c-1982c4, #1982C4)",
                    "borderRadius": "9px",
                    "background": "var(--od-c-1982c4, #1982C4)"
                  }}
                  role="group">
                <button type="button"
                        style={{"borderTopLeftRadius": "8px", "borderBottomLeftRadius": "8px"}}
                        onClick={
                          (e) => {
                            e.preventDefault();
                            var updated = Object.assign({}, dealBracket, {dollar_or_percentage_operator: "AND"});

                            dealBracketChanged(updated);
                            saveDealBracket(csrfToken, team, updated);
                          }
                        }
                        className={"btn strong " + (dealBracket.dollar_or_percentage_operator === 'AND' ? "btn-primary" : "btn-external-inverse")}>
                  <i className='fa-solid fa-plus mx-1'></i>
                </button>
                <button type="button"
                        style={{"borderTopRightRadius": "8px", "borderBottomRightRadius": "8px"}}
                        onClick={
                          (e) => {
                            e.preventDefault();
                            var updated = Object.assign({}, dealBracket, {dollar_or_percentage_operator: "OR"});

                            dealBracketChanged(updated);
                            saveDealBracket(csrfToken, team, updated);
                          }
                        }
                        className={"btn strong " + (dealBracket.dollar_or_percentage_operator === 'OR' ? "btn-primary" : "btn-external-inverse")}>
                  VS
                </button>
              </div>
            </div>
            <div className="col-xs-auto"
                  style={{"maxWidth": "160px"}}>
              <div className="form-group mb-0">
                <label className="text-muted"
                      style={{"whiteSpace": "nowrap"}}
                      htmlFor="offer-upside-percentage">
                  Percentage of ticket sales
                </label>
                <div className="input-group inline-white"
                    style={{
                      "border": "1px solid var(--od-c-e9eaeb, #e6e6e6)",
                      "borderRadius": "4px"
                    }}>
                  <CurrencyInput
                    id="offer-upside-percentage"
                    aria-describedby="offer-upside-percentage-percentage"
                    className="form-control"
                    maskOptions={{ prefix: "", integerLimit: null }}
                    placeholder={"0.00"}
                    value={upsidePercentage || ""}
                    onChange={e => {
                      var newValue = e.target.value;
                      setUpsidePercentage(newValue);

                      var changes = { upside_percentage: newValue };
                      var updated = { ...dealBracket, ...changes };

                      dealBracketChanged(updated);
                      debouncedSaveDealBracket(csrfToken, team, dealBracket, changes);
                    }}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text px-2"
                          id="offer-upside-percentage-percentage">
                      <i className="far fa-percentage text-muted"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xs-auto position-relative">
              <a href="#"
                 onClick={
                   (e) => {
                     e.preventDefault();

                     var updatedDealBrackets = dealBrackets.filter((db) =>
                       db.id !== dealBracket.id
                     );

                     dealBracketsChanged(updatedDealBrackets);
                     deleteDealBracketRecord(csrfToken, team, dealBracket);
                   }
                 }
                 style={{
                   "bottom": "7px",
                   "left": "10px",
                   "color": "var(--od-c-a4a7ae, #cacaca)"
                 }}
                 className="position-absolute">
                <i className="fas fa-trash-alt"></i>
              </a>
            </div>
          </div>
          {hasPayoutCap(offer?.artist_payout_cap) && (
            <div className="d-flex align-items-center"
                 style={{
                   marginTop: "-6px",
                   marginBottom: "10px",
                   marginLeft: "38px",
                   paddingLeft: "10px",
                   paddingRight: "10px",
                   paddingTop: "6px",
                   paddingBottom: "6px",
                   fontSize: "12px",
                   color: "var(--od-c-717680, #57606a)",
                   background: "var(--od-c-fafafa, #f6f8fa)",
                   borderLeft: "3px solid #54aeff",
                   borderRadius: "0 6px 6px 0",
                 }}>
              <span>↳ On cap exceeded, route overflow to:</span>
              <select
                style={{ marginLeft: "auto", border: "1px solid var(--od-c-d5d7da, #d0d7de)", borderRadius: "4px", padding: "2px 8px", background: "var(--od-c-ffffff, white)" }}
                value={dealBracket.cap_overflow_target || ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? null : e.target.value;
                  const updated = Object.assign({}, dealBracket, { cap_overflow_target: value });
                  dealBracketChanged(updated);
                  saveDealBracket(csrfToken, team, updated);
                }}
              >
                <option value="">None</option>
                <option value="headliner">Headliner</option>
                <option value="venue">Venue</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  )
};

DealBracket.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object,
  showSecondaryDollarAmountField: PropTypes.bool.isRequired,
  dealBracket: PropTypes.object.isRequired,
  dealBracketChanged: PropTypes.func.isRequired,
  dealBrackets: PropTypes.array.isRequired,
  dealBracketsChanged: PropTypes.func.isRequired,
  isFirstBracket: PropTypes.bool,
  index: PropTypes.number,
  provided: PropTypes.object,
  snapshot: PropTypes.object
};

export default DealBracket;
